import {useCallback, useRef} from 'react'
import type {ChatInputRef} from '@github-ui/copilot-shared-ui/components/chat-input-ref'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import type {IcebreakerOption} from '@github-ui/copilot-shared-ui/components/icebreaker'

const DEFAULT_DISMISSED_KEY = 'AGENT_ICEBREAKERS_DISMISSED'

interface UseIcebreakersOptions {
  /** Analytics location identifier. */
  location: string
  /** Analytics variant label. */
  variant?: string
  /** Custom localStorage key for dismiss state. Defaults to AGENT_ICEBREAKERS_DISMISSED. */
  dismissedKey?: string
  /**
   * Custom select handler. When provided, the hook will NOT set text on a chat
   * input ref — the caller owns the selection behavior (e.g. navigation).
   */
  onSelect?: (option: IcebreakerOption) => void
  /**
   * Externally-owned ref for the input's textarea. Provide this when a parent
   * needs to focus the input (e.g. from a sibling). Created internally when
   * omitted.
   */
  textAreaRef?: React.RefObject<HTMLTextAreaElement | null>
}

export function useIcebreakers({
  location,
  variant,
  dismissedKey,
  onSelect,
  textAreaRef: externalTextAreaRef,
}: UseIcebreakersOptions) {
  const storageKey = dismissedKey ?? DEFAULT_DISMISSED_KEY
  const chatInputRef = useRef<ChatInputRef | null>(null)
  const internalTextAreaRef = useRef<HTMLTextAreaElement | null>(null)
  const textAreaRef = externalTextAreaRef ?? internalTextAreaRef
  const selectedIcebreakerRef = useRef<IcebreakerOption | null>(null)
  const [dismissed, setDismissed] = useLocalStorage(storageKey, false)

  const visible = !dismissed

  const handleSelect = useCallback(
    (option: IcebreakerOption) => {
      selectedIcebreakerRef.current = option
      sendEvent('agent_sessions.icebreaker_click', {
        location,
        variant: variant ?? 'control',
        icebreaker_title: option.title,
      })
      if (onSelect) {
        onSelect(option)
        return
      }
      chatInputRef.current?.setText(option.prompt)
      requestAnimationFrame(() => {
        const textarea = textAreaRef.current
        if (textarea) {
          textarea.focus()
          textarea.setSelectionRange(textarea.value.length, textarea.value.length)
        }
      })
    },
    [location, variant, onSelect, textAreaRef],
  )

  const handleDismiss = useCallback(() => {
    sendEvent('agent_sessions.icebreaker_dismiss', {
      location,
      variant: variant ?? 'control',
    })
    setDismissed(true)
  }, [location, variant, setDismissed])

  const handleIcebreakerSubmit = useCallback(
    (submittedPrompt: string) => {
      if (selectedIcebreakerRef.current) {
        const wasEdited = submittedPrompt.trim() !== selectedIcebreakerRef.current.prompt.trim()
        sendEvent('agent_sessions.icebreaker_submit', {
          location,
          variant: variant ?? 'control',
          icebreaker_title: selectedIcebreakerRef.current.title,
          prompt_was_edited: wasEdited,
        })
        selectedIcebreakerRef.current = null
      }
    },
    [location, variant],
  )

  return {
    chatInputRef,
    textAreaRef,
    visible,
    handleSelect,
    handleDismiss,
    handleIcebreakerSubmit,
  }
}
